import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingCart, Package, Truck, CheckCircle, Clock, XCircle,
    X, Eye, Users, DollarSign, MapPin, AlertCircle, UserCheck,
    Search, Filter, Download, ChevronDown, Phone, Mail, User,
    Calendar, FileText, Edit, Printer, RefreshCw, BarChart2,
    TrendingUp, ArrowRight, Check, Copy, ExternalLink, ImageIcon
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DataTable from '../components/dashboard/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';
import OrderInvoice from '../components/OrderInvoice';

const AdminOrders = () => {
    const { token } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [availableVendors, setAvailableVendors] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [assignmentNotes, setAssignmentNotes] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [vendorFilter, setVendorFilter] = useState('all');
    const [orderNote, setOrderNote] = useState('');
    const [powNotes, setPowNotes] = useState('');
    const [updating, setUpdating] = useState(false);

    const printRef = useRef();
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Invoice-${selectedOrder?.id || 'Order'}`,
    });

    const isInitialLoad = useRef(true);

    const fetchOrders = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(res.data);
        } catch (error) {
            console.log('Using demo data');
            setOrders([
                {
                    id: 'ORD-001', user_id: 'u1', status: 'pending', total_amount: 85000,
                    shipping_address: '123 Solar St, Green City, Delhi 110001',
                    created_at: '2024-12-27T10:30:00Z',
                    customer: { name: 'Rahul Sharma', email: 'rahul@email.com', phone: '+91 98765 43210' },
                    items: [{ product_id: '1', name: 'Solar Panel 400W Mono', quantity: 10, price: 8500 }],
                    timeline: [
                        { status: 'created', timestamp: '2024-12-27T10:30:00Z', note: 'Order placed by customer' }
                    ]
                },
                {
                    id: 'ORD-002', user_id: 'u2', status: 'assigned', total_amount: 150000,
                    shipping_address: '456 Energy Ave, Mumbai 400001',
                    created_at: '2024-12-26T14:00:00Z',
                    assigned_vendor_id: 'v1', assigned_vendor_name: 'SunPower Solar',
                    customer: { name: 'Priya Patel', email: 'priya@business.com', phone: '+91 99887 66554' },
                    items: [{ product_id: '3', name: '10kW Commercial System', quantity: 1, price: 150000 }],
                    timeline: [
                        { status: 'created', timestamp: '2024-12-26T14:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-26T15:30:00Z', note: 'Assigned to SunPower Solar' }
                    ]
                },
                {
                    id: 'ORD-003', user_id: 'u3', status: 'processing', total_amount: 32000,
                    shipping_address: '789 Green Blvd, Bangalore 560001',
                    created_at: '2024-12-25T09:00:00Z',
                    assigned_vendor_id: 'v2', assigned_vendor_name: 'Tesla Energy',
                    customer: { name: 'Vikram Singh', email: 'vikram@home.com', phone: '+91 88776 54321' },
                    items: [{ product_id: '4', name: 'Lithium Battery 10kWh', quantity: 1, price: 32000 }],
                    tracking_number: 'SHIP123456789',
                    carrier: 'BlueDart',
                    timeline: [
                        { status: 'created', timestamp: '2024-12-25T09:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-25T10:00:00Z', note: 'Assigned to Tesla Energy' },
                        { status: 'processing', timestamp: '2024-12-26T09:00:00Z', note: 'Vendor started processing' }
                    ]
                },
                {
                    id: 'ORD-004', user_id: 'u4', status: 'shipped', total_amount: 18000,
                    shipping_address: '321 Power Lane, Chennai 600001',
                    created_at: '2024-12-24T11:00:00Z',
                    assigned_vendor_id: 'v3', assigned_vendor_name: 'LG Solar',
                    customer: { name: 'Ananya Reddy', email: 'ananya@email.com', phone: '+91 77665 44332' },
                    items: [{ product_id: '5', name: 'Hybrid Inverter 5kW', quantity: 1, price: 18000 }],
                    tracking_number: 'DELHIVERY987654321',
                    carrier: 'Delhivery',
                    expected_delivery: '2024-12-30T18:00:00Z',
                    timeline: [
                        { status: 'created', timestamp: '2024-12-24T11:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-24T12:00:00Z', note: 'Assigned to LG Solar' },
                        { status: 'processing', timestamp: '2024-12-25T09:00:00Z', note: 'Preparing shipment' },
                        { status: 'shipped', timestamp: '2024-12-26T14:00:00Z', note: 'Shipped via Delhivery' }
                    ]
                },
                {
                    id: 'ORD-005', user_id: 'u5', status: 'delivered', total_amount: 250000,
                    shipping_address: '555 Eco Way, Hyderabad 500001',
                    created_at: '2024-12-20T08:00:00Z',
                    assigned_vendor_id: 'v1', assigned_vendor_name: 'SunPower Solar',
                    customer: { name: 'Karthik Nair', email: 'karthik@business.com', phone: '+91 99001 22334' },
                    items: [{ product_id: '6', name: '15kW Commercial System', quantity: 1, price: 250000 }],
                    tracking_number: 'FEDEX112233445',
                    carrier: 'FedEx',
                    delivered_at: '2024-12-28T10:30:00Z',
                    timeline: [
                        { status: 'created', timestamp: '2024-12-20T08:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-20T09:00:00Z', note: 'Assigned to SunPower Solar' },
                        { status: 'processing', timestamp: '2024-12-21T10:00:00Z', note: 'Order in production' },
                        { status: 'shipped', timestamp: '2024-12-25T09:00:00Z', note: 'Shipped via FedEx' },
                        { status: 'delivered', timestamp: '2024-12-28T10:30:00Z', note: 'Delivered successfully' }
                    ]
                },
                {
                    id: 'ORD-006', user_id: 'u6', status: 'completed', total_amount: 45000,
                    shipping_address: '777 Solar Dr, Pune 411001',
                    created_at: '2024-12-15T15:00:00Z',
                    assigned_vendor_id: 'v2', assigned_vendor_name: 'Tesla Energy',
                    customer: { name: 'Meera Joshi', email: 'meera@home.com', phone: '+91 88997 77665' },
                    items: [{ product_id: '1', name: 'Solar Panel 400W Mono', quantity: 15, price: 3000 }],
                    delivered_at: '2024-12-22T11:00:00Z',
                    installation_date: '2024-12-25T10:00:00Z',
                    timeline: [
                        { status: 'created', timestamp: '2024-12-15T15:00:00Z', note: 'Order placed' },
                        { status: 'assigned', timestamp: '2024-12-15T16:00:00Z', note: 'Assigned' },
                        { status: 'shipped', timestamp: '2024-12-18T09:00:00Z', note: 'Shipped' },
                        { status: 'delivered', timestamp: '2024-12-22T11:00:00Z', note: 'Delivered' },
                        { status: 'completed', timestamp: '2024-12-25T16:00:00Z', note: 'Installation completed' }
                    ]
                }
            ]);
        }
        if (!silent) setLoading(false);
    }, [token]);

    useEffect(() => {
        fetchOrders();
        isInitialLoad.current = false;
        const interval = setInterval(() => fetchOrders(true), 30000);
        return () => clearInterval(interval);
    }, [fetchOrders]);

    const fetchAvailableVendors = async (orderId) => {
        try {
            const res = await axios.get(`${API}/api/admin/orders/${orderId}/available-vendors`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableVendors(res.data.available_vendors);
        } catch (error) {
            setAvailableVendors([
                { vendor_id: 'v1', vendor_name: 'SunPower Solar', total_vendor_price: 72000, location: 'Delhi', email: 'sunpower@vendor.com', rating: 4.8 },
                { vendor_id: 'v2', vendor_name: 'Tesla Energy Store', total_vendor_price: 75000, location: 'Mumbai', email: 'tesla@vendor.com', rating: 4.9 },
                { vendor_id: 'v3', vendor_name: 'LG Solar Shop', total_vendor_price: 78000, location: 'Bangalore', email: 'lg@vendor.com', rating: 4.7 },
            ]);
        }
    };

    const handleAssignClick = async (order) => {
        setSelectedOrder(order);
        await fetchAvailableVendors(order.id);
        setShowAssignModal(true);
    };

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setShowDetailModal(true);
    };

    const handleAssignVendor = async (vendorId, vendorName) => {
        try {
            await axios.put(`${API}/api/admin/orders/${selectedOrder.id}/assign`,
                { vendor_id: vendorId, assignment_notes: assignmentNotes },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Order assigned to ${vendorName}`);
            setShowAssignModal(false);
            fetchOrders();
        } catch (error) {
            setOrders(orders.map(o =>
                o.id === selectedOrder.id
                    ? { ...o, status: 'assigned', assigned_vendor_id: vendorId, assigned_vendor_name: vendorName }
                    : o
            ));
            toast.success(`Order assigned to ${vendorName}`);
            setShowAssignModal(false);
        }
        setAssignmentNotes('');
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            await axios.put(`${API}/api/orders/${orderId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Order status updated to ${newStatus}`);
            fetchOrders();
        } catch (error) {
            setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
            toast.success(`Order status updated to ${newStatus}`);
        }
        setShowDetailModal(false);
    };

    const addOrderNote = async () => {
        if (!orderNote.trim()) return;
        toast.success('Note added to order');
        setOrderNote('');
    };

    const handleApproveRejectPow = async (status) => {
        if (status === 'rejected' && !powNotes.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        try {
            setUpdating(true);
            await axios.put(`${API}/api/admin/orders/${selectedOrder.id}/proof-of-work-status`,
                { status, notes: status === 'rejected' ? powNotes : null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Proof of work ${status}`);
            
            const updatedOrder = { 
                ...selectedOrder, 
                proof_of_work_status: status, 
                proof_of_work_notes: status === 'rejected' ? powNotes : null 
            };
            setSelectedOrder(updatedOrder);
            setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
            setPowNotes('');
        } catch (error) {
            console.error('Failed to update proof of work status', error);
            toast.error(error.response?.data?.detail || `Failed to ${status} proof of work`);
        } finally {
            setUpdating(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const exportOrders = () => {
        const csv = orders.map(o =>
            `${o.id},${o.customer?.name || 'N/A'},${o.total_amount},${o.status},${o.created_at}`
        ).join('\n');
        const blob = new Blob([`Order ID,Customer,Amount,Status,Date\n${csv}`], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        toast.success('Orders exported to CSV');
    };

    const getStatusConfig = (status) => {
        const configs = {
            pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: 'Pending' },
            assigned: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: UserCheck, label: 'Assigned' },
            processing: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Package, label: 'Processing' },
            shipped: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Truck, label: 'Shipped' },
            delivered: { color: 'bg-teal-100 text-teal-700 border-teal-200', icon: CheckCircle, label: 'Delivered' },
            completed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Completed' },
            cancelled: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Cancelled' }
        };
        return configs[status] || configs.pending;
    };

    const canEditVendor = (status) => !['shipped', 'delivered', 'completed', 'cancelled'].includes(status);
    const getShortId = (id) => `#${(id || '').substring(0, 6).toUpperCase()}`;

    // Filtered orders
    const filteredOrders = orders.filter(order => {
        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
        const matchesSearch = searchQuery === '' ||
            order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.customer?.email?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesVendor = vendorFilter === 'all' || order.assigned_vendor_id === vendorFilter;
        return matchesStatus && matchesSearch && matchesVendor;
    });

    // Stats
    const stats = {
        total: orders.length,
        pending: orders.filter(o => o.status === 'pending').length,
        processing: orders.filter(o => ['assigned', 'processing'].includes(o.status)).length,
        shipped: orders.filter(o => o.status === 'shipped').length,
        completed: orders.filter(o => ['delivered', 'completed'].includes(o.status)).length,
        revenue: orders.reduce((sum, o) => sum + (o.total_amount || 0), 0)
    };

    const uniqueVendors = [...new Set(orders.filter(o => o.assigned_vendor_id).map(o => ({ id: o.assigned_vendor_id, name: o.assigned_vendor_name })))];

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold">Order Management</h1>
                        <p className="text-muted-foreground mt-1">Manage and track all customer orders</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchOrders}>
                            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                        </Button>
                        <Button variant="outline" onClick={exportOrders}>
                            <Download className="w-4 h-4 mr-2" /> Export
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="bg-card rounded-xl border p-4">
                        <p className="text-xs text-muted-foreground">Total Orders</p>
                        <p className="text-2xl font-bold">{stats.total}</p>
                    </div>
                    <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
                        <p className="text-xs text-yellow-700">Pending</p>
                        <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                        <p className="text-xs text-blue-700">Processing</p>
                        <p className="text-2xl font-bold text-blue-700">{stats.processing}</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
                        <p className="text-xs text-purple-700">Shipped</p>
                        <p className="text-2xl font-bold text-purple-700">{stats.shipped}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                        <p className="text-xs text-green-700">Completed</p>
                        <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
                    </div>
                    <div className="bg-primary/10 rounded-xl border border-primary/20 p-4">
                        <p className="text-xs text-primary">Total Revenue</p>
                        <p className="text-2xl font-bold text-primary">₹{(stats.revenue / 100000).toFixed(1)}L</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-card rounded-xl border p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by Order ID, Customer..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full md:w-[180px]">
                                <SelectValue placeholder="All Statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="assigned">Assigned</SelectItem>
                                <SelectItem value="processing">Processing</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={vendorFilter} onValueChange={setVendorFilter}>
                            <SelectTrigger className="w-full md:w-[180px]">
                                <SelectValue placeholder="All Vendors" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Vendors</SelectItem>
                                {uniqueVendors.map(v => (
                                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Orders Table */}
                <div className="bg-card rounded-xl border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-muted/50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Order</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Customer</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Placed By</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Items</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Amount</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Vendor</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                                    <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                                ) : filteredOrders.length === 0 ? (
                                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No orders found</td></tr>
                                ) : (
                                    filteredOrders.map((order) => {
                                        const statusConfig = getStatusConfig(order.status);
                                        const StatusIcon = statusConfig.icon;
                                        return (
                                            <tr key={order.id} className="border-t hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => handleViewDetails(order)}>
                                                <td className="px-4 py-3">
                                                    <p className="font-mono font-semibold text-primary">{getShortId(order.id)}</p>
                                                    <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</p>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="font-medium">{order.customer?.name || order.customer_name_override || 'N/A'}</p>
                                                    <p className="text-xs text-muted-foreground">{order.customer?.email}</p>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {order.order_source === 'salesman' ? (
                                                        <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 dark:bg-orange-900/30 rounded-full px-2 py-0.5 text-xs font-medium">
                                                            🧑‍💼 {order.placed_by_salesman_name || 'Salesman'}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">Customer</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="text-sm">{order.items?.length || 0} item(s)</p>
                                                    <p className="text-xs text-muted-foreground truncate max-w-[150px]">{order.items?.[0]?.name}</p>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="font-semibold">₹{order.total_amount?.toLocaleString()}</p>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge className={`${statusConfig.color} border`}>
                                                        <StatusIcon className="w-3 h-3 mr-1" />
                                                        {statusConfig.label}
                                                    </Badge>
                                                    {(order.pow_delivery_url || order.pow_bill_url || order.pow_agreement_url) && (
                                                        <div className="mt-1">
                                                            <Badge variant="outline" className={`text-[9px] uppercase ${order.proof_of_work_status === 'approved' ? 'bg-green-100 text-green-700' : order.proof_of_work_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                                PoW: {order.proof_of_work_status || 'PENDING'}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {order.assigned_vendor_name ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm">{order.assigned_vendor_name}</span>
                                                            {canEditVendor(order.status) && (
                                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); handleAssignClick(order); }}>
                                                                    <Edit className="w-3 h-3" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleAssignClick(order); }}>
                                                            Assign
                                                        </Button>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                                    {new Date(order.created_at).toLocaleDateString()}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetails(order); }}>
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Order Detail Modal */}
                <AnimatePresence>
                    {showDetailModal && selectedOrder && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
                            onClick={() => setShowDetailModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 20 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-5xl my-8"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="p-6 border-b flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold font-mono">{getShortId(selectedOrder.id)}</h2>
                                            <Badge className={`${getStatusConfig(selectedOrder.status).color} border`}>
                                                {getStatusConfig(selectedOrder.status).label}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Placed on {new Date(selectedOrder.created_at).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(selectedOrder.id)}>
                                            <Copy className="w-4 h-4 mr-1" /> Copy ID
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => setShowDetailModal(false)}>
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="p-6 grid md:grid-cols-3 gap-6">
                                    {/* Left Column - Customer & Shipping */}
                                    <div className="space-y-4">
                                        {/* Customer Info */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <User className="w-4 h-4" /> Customer
                                            </h3>
                                            <p className="font-medium">{selectedOrder.customer?.name || 'N/A'}</p>
                                            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                                                <p className="flex items-center gap-2">
                                                    <Mail className="w-3 h-3" /> {selectedOrder.customer?.email}
                                                </p>
                                                <p className="flex items-center gap-2">
                                                    <Phone className="w-3 h-3" /> {selectedOrder.customer?.phone}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Shipping Address */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <MapPin className="w-4 h-4" /> Shipping Address
                                            </h3>
                                            <p className="text-sm">
                                                {typeof selectedOrder.shipping_address === 'object' && selectedOrder.shipping_address
                                                    ? `${selectedOrder.shipping_address.address}, ${selectedOrder.shipping_address.city}, ${selectedOrder.shipping_address.state} - ${selectedOrder.shipping_address.pincode}`
                                                    : selectedOrder.shipping_address}
                                            </p>
                                            <Button variant="link" size="sm" className="p-0 h-auto mt-2" asChild>
                                                <a href={`https://maps.google.com/?q=${encodeURIComponent(
                                                    typeof selectedOrder.shipping_address === 'object' && selectedOrder.shipping_address
                                                        ? `${selectedOrder.shipping_address.address}, ${selectedOrder.shipping_address.city}, ${selectedOrder.shipping_address.state}`
                                                        : selectedOrder.shipping_address
                                                )}`} target="_blank" rel="noopener noreferrer">
                                                    <ExternalLink className="w-3 h-3 mr-1" /> View on Map
                                                </a>
                                            </Button>
                                        </div>

                                        {/* Vendor Info */}
                                        {selectedOrder.assigned_vendor_name ? (
                                            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                                <h3 className="font-semibold flex items-center gap-2 mb-2 text-green-700">
                                                    <CheckCircle className="w-4 h-4" /> Assigned Vendor
                                                </h3>
                                                <p className="font-medium">{selectedOrder.assigned_vendor_name}</p>
                                                {canEditVendor(selectedOrder.status) && (
                                                    <Button variant="outline" size="sm" className="mt-2" onClick={() => { setShowDetailModal(false); handleAssignClick(selectedOrder); }}>
                                                        Reassign Vendor
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                                                <h3 className="font-semibold flex items-center gap-2 mb-2 text-yellow-700">
                                                    <AlertCircle className="w-4 h-4" /> No Vendor Assigned
                                                </h3>
                                                <Button size="sm" onClick={() => { setShowDetailModal(false); handleAssignClick(selectedOrder); }}>
                                                    Assign Vendor
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Middle Column - Order Items & Payment */}
                                    <div className="space-y-4">
                                        {/* Order Items */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <Package className="w-4 h-4" /> Order Items
                                            </h3>
                                            <div className="space-y-3">
                                                {selectedOrder.items?.map((item, idx) => (
                                                    <div key={idx} className="flex justify-between items-center p-2 bg-card rounded-lg">
                                                        <div>
                                                            <p className="font-medium text-sm">{item.name}</p>
                                                            <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                                                        </div>
                                                        <p className="font-semibold">₹{(item.price * item.quantity).toLocaleString()}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Payment Summary */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <DollarSign className="w-4 h-4" /> Payment Summary
                                            </h3>
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Subtotal</span>
                                                    <span>₹{selectedOrder.total_amount?.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Shipping</span>
                                                    <span className="text-green-600">FREE</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">GST (18%)</span>
                                                    <span>₹{Math.round(selectedOrder.total_amount * 0.18).toLocaleString()}</span>
                                                </div>
                                                <hr className="my-2" />
                                                <div className="flex justify-between font-bold text-lg">
                                                    <span>Total</span>
                                                    <span className="text-primary">₹{Math.round(selectedOrder.total_amount * 1.18).toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tracking */}
                                        {selectedOrder.tracking_number && (
                                            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                                                <h3 className="font-semibold flex items-center gap-2 mb-2 text-purple-700">
                                                    <Truck className="w-4 h-4" /> Shipment Tracking
                                                </h3>
                                                <p className="text-sm font-mono">{selectedOrder.tracking_number}</p>
                                                <p className="text-xs text-muted-foreground">via {selectedOrder.carrier}</p>
                                                {selectedOrder.expected_delivery && (
                                                    <p className="text-xs text-green-600 mt-1">Expected: {new Date(selectedOrder.expected_delivery).toLocaleDateString()}</p>
                                                )}
                                            </div>
                                        )}

                                    </div>

                                    {/* Right Column - Timeline & Actions */}
                                    <div className="space-y-4">
                                        {/* Order Timeline */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <Calendar className="w-4 h-4" /> Order Timeline
                                            </h3>
                                            <div className="space-y-4">
                                                {(selectedOrder.timeline || []).map((event, idx) => {
                                                    const config = getStatusConfig(event.status);
                                                    const EventIcon = config.icon;
                                                    return (
                                                        <div key={idx} className="flex gap-3">
                                                            <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center flex-shrink-0`}>
                                                                <EventIcon className="w-4 h-4" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="font-medium text-sm capitalize">{event.status}</p>
                                                                <p className="text-xs text-muted-foreground">{event.note}</p>
                                                                <p className="text-xs text-muted-foreground">{new Date(event.timestamp).toLocaleString()}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Quick Actions */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold mb-3">Quick Actions</h3>
                                            <div className="space-y-2">
                                                {selectedOrder.status === 'pending' && (
                                                    <Button className="w-full" onClick={() => { setShowDetailModal(false); handleAssignClick(selectedOrder); }}>
                                                        <Users className="w-4 h-4 mr-2" /> Assign Vendor
                                                    </Button>
                                                )}
                                                {selectedOrder.status === 'delivered' && (
                                                    <Button className="w-full" onClick={() => updateOrderStatus(selectedOrder.id, 'completed')}>
                                                        <CheckCircle className="w-4 h-4 mr-2" /> Mark as Completed
                                                    </Button>
                                                )}
                                                <Button variant="outline" className="w-full" onClick={() => handlePrint()}>
                                                    <Printer className="w-4 h-4 mr-2" /> Print Invoice
                                                </Button>
                                                <Button variant="outline" className="w-full text-red-600 hover:bg-red-50" onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}>
                                                    <XCircle className="w-4 h-4 mr-2" /> Cancel Order
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Add Note */}
                                        <div className="bg-muted/30 rounded-xl p-4">
                                            <h3 className="font-semibold flex items-center gap-2 mb-3">
                                                <FileText className="w-4 h-4" /> Add Note
                                            </h3>
                                            <Textarea
                                                placeholder="Add internal note..."
                                                value={orderNote}
                                                onChange={e => setOrderNote(e.target.value)}
                                                rows={2}
                                            />
                                            <Button size="sm" className="mt-2" onClick={addOrderNote}>Add Note</Button>
                                        </div>
                                    </div>
                                    </div>

                                    {/* Proof of Work Admin Review Multi-Upload Section */}
                                    {(selectedOrder.pow_delivery_url || selectedOrder.pow_bill_url || selectedOrder.pow_agreement_url) && (
                                        <div className="bg-muted/10 border-t p-6 md:col-span-2">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-bold flex items-center gap-2">
                                                    <ImageIcon className="w-5 h-5 text-primary" /> Vendor Uploaded Proof
                                                </h3>
                                                <Badge variant="outline" className={`px-3 py-1 text-xs uppercase ${
                                                    selectedOrder.proof_of_work_status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                                    selectedOrder.proof_of_work_status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                                                    'bg-yellow-100 text-yellow-700 border-yellow-200'
                                                }`}>
                                                    Status: {selectedOrder.proof_of_work_status || 'PENDING'}
                                                </Badge>
                                            </div>

                                            <div className="grid md:grid-cols-3 gap-6 mb-6">
                                                {/* Delivery */}
                                                <div className="bg-card rounded-xl border p-3 flex flex-col">
                                                    <h4 className="font-semibold text-sm mb-3">1. Proof of Delivery</h4>
                                                    {selectedOrder.pow_delivery_url ? (
                                                        <div className="flex-1 rounded-lg overflow-hidden border">
                                                            <a href={selectedOrder.pow_delivery_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_delivery_url}` : selectedOrder.pow_delivery_url} target="_blank" rel="noopener noreferrer">
                                                                <img src={selectedOrder.pow_delivery_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_delivery_url}` : selectedOrder.pow_delivery_url} alt="Proof of Delivery" className="w-full h-40 object-cover hover:opacity-90 transition-opacity" />
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-lg bg-muted/30">
                                                            <X className="w-8 h-8 text-muted-foreground/50 mb-2" />
                                                            <span className="text-xs text-muted-foreground">Not Provided</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Bill */}
                                                <div className="bg-card rounded-xl border p-3 flex flex-col">
                                                    <h4 className="font-semibold text-sm mb-3">2. Customer Bill</h4>
                                                    {selectedOrder.pow_bill_url ? (
                                                        <div className="flex-1 rounded-lg overflow-hidden border">
                                                            <a href={selectedOrder.pow_bill_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_bill_url}` : selectedOrder.pow_bill_url} target="_blank" rel="noopener noreferrer">
                                                                <img src={selectedOrder.pow_bill_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_bill_url}` : selectedOrder.pow_bill_url} alt="Customer Bill" className="w-full h-40 object-cover hover:opacity-90 transition-opacity" />
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-lg bg-muted/30">
                                                            <X className="w-8 h-8 text-muted-foreground/50 mb-2" />
                                                            <span className="text-xs text-muted-foreground">Not Provided</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Agreement */}
                                                <div className="bg-card rounded-xl border p-3 flex flex-col">
                                                    <h4 className="font-semibold text-sm mb-3">3. Customer Agreement</h4>
                                                    {selectedOrder.pow_agreement_url ? (
                                                        <div className="flex-1 rounded-lg overflow-hidden border">
                                                            <a href={selectedOrder.pow_agreement_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_agreement_url}` : selectedOrder.pow_agreement_url} target="_blank" rel="noopener noreferrer">
                                                                <img src={selectedOrder.pow_agreement_url.startsWith('/uploads') ? `${API}${selectedOrder.pow_agreement_url}` : selectedOrder.pow_agreement_url} alt="Customer Agreement" className="w-full h-40 object-cover hover:opacity-90 transition-opacity" />
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-lg bg-muted/30">
                                                            <X className="w-8 h-8 text-muted-foreground/50 mb-2" />
                                                            <span className="text-xs text-muted-foreground">Not Provided</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Action Box */}
                                            {selectedOrder.proof_of_work_status === 'pending' && (
                                                <div className="bg-card border rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-semibold text-sm mb-3">Administrator Review</h4>
                                                    <div className="space-y-4">
                                                        <div>
                                                            <Label className="text-xs">Rejection Reason (Required if rejecting)</Label>
                                                            <Textarea 
                                                                placeholder="Enter reason if rejecting (will be shared with vendor)..."
                                                                value={powNotes}
                                                                onChange={e => setPowNotes(e.target.value)}
                                                                rows={2}
                                                                maxLength={500}
                                                                className="mt-1"
                                                            />
                                                        </div>
                                                        <div className="flex gap-3">
                                                            <Button size="lg" className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50" onClick={() => handleApproveRejectPow('approved')} disabled={updating}>
                                                                <Check className="w-4 h-4 mr-2" /> Approve All
                                                            </Button>
                                                            <Button size="lg" className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50" onClick={() => handleApproveRejectPow('rejected')} disabled={updating}>
                                                                <X className="w-4 h-4 mr-2" /> Reject Documents
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {selectedOrder.proof_of_work_status === 'rejected' && selectedOrder.proof_of_work_notes && (
                                                <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200">
                                                    <strong className="block mb-1">Previously Rejected with Note:</strong>
                                                    <p className="text-sm">{selectedOrder.proof_of_work_notes}</p>
                                                    <p className="text-xs mt-2 italic text-red-600/80">Waiting for vendor to replace documents.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Assign Vendor Modal */}
                <AnimatePresence>
                    {showAssignModal && selectedOrder && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                            onClick={() => setShowAssignModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.9 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b flex items-center justify-between">
                                    <div>
                                        <h2 className="text-xl font-bold">Assign Vendor</h2>
                                        <p className="text-sm text-muted-foreground">Order {getShortId(selectedOrder.id)} • ₹{selectedOrder.total_amount?.toLocaleString()}</p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => setShowAssignModal(false)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="space-y-2">
                                        <Label>Assignment Notes (Optional)</Label>
                                        <Textarea
                                            placeholder="Add any special instructions for the vendor..."
                                            value={assignmentNotes}
                                            onChange={e => setAssignmentNotes(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <Label>Select Vendor</Label>
                                        {availableVendors.length === 0 ? (
                                            <div className="text-center py-6 text-muted-foreground">
                                                <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                                                <p>No vendors available. Make sure vendors are approved.</p>
                                            </div>
                                        ) : availableVendors.map(vendor => (
                                            <div key={vendor.vendor_id} className={`p-4 border rounded-xl hover:border-primary transition-colors cursor-pointer ${vendor.note ? 'border-yellow-300 bg-yellow-50/50' : ''}`} onClick={() => handleAssignVendor(vendor.vendor_id, vendor.vendor_name)}>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-semibold">{vendor.vendor_name}</p>
                                                        <p className="text-sm text-muted-foreground">{vendor.location} • {vendor.email}</p>
                                                        {vendor.phone && <p className="text-sm text-muted-foreground">{vendor.phone}</p>}
                                                        {vendor.note && <p className="text-xs text-yellow-600 mt-1">⚠️ {vendor.note}</p>}
                                                    </div>
                                                    <div className="text-right">
                                                        {vendor.total_vendor_price > 0 ? (
                                                            <p className="font-bold text-primary">₹{vendor.total_vendor_price?.toLocaleString()}</p>
                                                        ) : (
                                                            <Badge variant="outline" className="text-xs">Manual</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Hidden Invoice for Printing */}
                <div className="hidden">
                    <OrderInvoice ref={printRef} order={selectedOrder} />
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AdminOrders;
